import { useEffect, useRef } from 'react';
import type { Insight } from '@duty/shared';
import { useSoundCues } from './useSoundCues';

/**
 * Fires an audio+visual alert whenever a new critical or high insight appears
 * that wasn't present in the previous render. Uses stable IDs so it fires once
 * per insight, not on every re-render.
 */
export function useInsightAlerts(insights: readonly Insight[]) {
  const { alert } = useSoundCues();
  const seenIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    const alarming = insights.filter(
      (i) =>
        (i.severity === 'critical' || i.severity === 'high') &&
        (i.status === 'open' || i.status === 'acknowledged'),
    );
    const newOnes = alarming.filter((i) => !seenIds.current.has(i.id));
    if (newOnes.length > 0) {
      alert();
    }
    // Rebuild seen set from all current open/acknowledged (not just alarming),
    // so dismissed-then-reopened cards re-alert correctly.
    seenIds.current = new Set(
      insights
        .filter((i) => i.status === 'open' || i.status === 'acknowledged')
        .map((i) => i.id),
    );
  }, [insights, alert]);
}
